import React, { useCallback, useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import Table from '../../../components/Table';
import SearchInput from '../../../components/SearchInput/SearchInput';
import Switch from '../../../components/form/switch';
import ApprovalStats from '../components/ApprovalStats';
import Pagination from '../../../components/Pagination/Pagination';
import SkeletonLoader from '../../../components/skeleton-loader';
import PowerappApprovalCard from './powerapp-card';
import styles from './style.module.scss';
import pageHeadingStyles from '../../../styles/PageHeader.module.scss';
import useCommonStore from '../../../stores/common';
import useUserStore from '../../../stores/user';
import useManagerApprovalStore from '../../../stores/managerApproval';
import { fetchPowerappsWithApprovalCounts, fetchArchivedApprovals } from '../../../services/managerApproval';
import toast from 'react-hot-toast';

const List = () => {
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const userOrgId = useUserStore((state) => state.organizationId);
  const isGodUser = useUserStore((state) => state.isGodUser);
  const {
    powerappsWithApprovals,
    archivedApprovals,
    searchQuery,
    sortBy,
    sortDirection,
    loading,
    currentPage,
    pageCount,
    itemsPerPage,
    setCurrentPage,
    setSearchQuery,
    setSortBy,
    setSortDirection,
    setLoading,
    setOrganizationId,
    organizationId,
    setSelectedPowerappId,
    selectedPowerappId,
    totalItems
  } = useManagerApprovalStore();

  const [showArchived, setShowArchived] = useState(false);
  const [stats, setStats] = useState({
    total_powerapps: 0,
    total_approvals: 0,
    summary: { approved: 0, declined: 0 }
  });
  const debounceTimeoutRef = useRef(null);

  const loadData = useCallback(async (orgId, pageParam = null, searchQueryParam = null, sortByParam = null, sortDirectionParam = null) => {
    try {
      setLoading(true);
      // Use provided params or fall back to store values
      const finalPage = pageParam !== null ? pageParam : currentPage;
      const finalSearchQuery = searchQueryParam !== null ? searchQueryParam : searchQuery;
      // When showArchived is true, default to 'last_updated_ago' if no explicit sortByParam is provided
      // If sortByParam is provided, use it (user explicitly sorted)
      let finalSortBy;
      if (sortByParam !== null) {
        finalSortBy = sortByParam;
      } else if (showArchived) {
        finalSortBy = 'last_updated_ago';
        setSortBy('last_updated_ago');
      } else {
        finalSortBy = sortBy;
      }
      const finalSortDirection = sortDirectionParam !== null ? sortDirectionParam : (sortDirection || 'desc');
      
      if (showArchived) {
        const response = await fetchArchivedApprovals({
          organization_id: orgId,
          page: finalPage,
          per_page: itemsPerPage,
          search: finalSearchQuery || '',
          sort: finalSortBy || '',
          sort_direction: finalSortDirection.toLowerCase()
        });
        if (response.status) {
          setStats({
            total_powerapps: response.total_powerapps || 0,
            total_approvals: response.total_approvals || 0,
            summary: response.summary || { approved: 0, declined: 0 }
          });
        } else {
          toast.error('Failed to load archived approvals', { duration: 5000 });
        }
      } else {
        const response = await fetchPowerappsWithApprovalCounts(orgId, {
          page: finalPage,
          per_page: itemsPerPage,
          search: finalSearchQuery || '',
          sort: finalSortBy || '',
          sort_direction: finalSortDirection.toLowerCase()
        });
        if (response.status) {
          setStats({
            total_powerapps: 0,
            total_approvals: 0,
            summary: { approved: 0, declined: 0 }
          });
        } else {
          toast.error('Failed to load approvals', { duration: 5000 });
        }
      }
    } catch (error) {
      console.error('Error loading approvals:', error);
      toast.error('Error loading approvals', { duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [showArchived, setLoading, currentPage, itemsPerPage, sortBy, sortDirection]);

  useEffect(() => {
    if (userOrgId && (!organizationId || organizationId !== userOrgId)) {
      setOrganizationId(userOrgId);
      loadData(userOrgId, currentPage, searchQuery, sortBy, sortDirection);
    }
  }, [userOrgId, organizationId, setOrganizationId, loadData, currentPage, sortBy, sortDirection]);

  // Reload data when switching between pending and archived
  useEffect(() => {
    if (organizationId) {
      // When showArchived is true, pass null for sortBy to use default 'last_updated_ago'
      // When showArchived is false, use sortBy from store
      const sortByForLoad = showArchived ? null : sortBy;
      loadData(organizationId, currentPage, searchQuery, sortByForLoad, sortDirection);
    }
  }, [showArchived, organizationId, loadData, currentPage, sortBy, sortDirection]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);


  const pendingColumns = [
    {
      headerName: 'DBA',
      field: 'powerapp_dba',
      width: '200px',
      allowSort: true,
    },
    ...(isGodUser ? [{
      headerName: 'Organization',
      field: 'organization_name',
      width: '200px',
      allowSort: true,
      render: (row) => row.organization_name || '-'
    }] : []),
    {
      headerName: 'Pending',
      field: 'pending_count',
      width: '100px',
      allowSort: true,
      render: (row) => row.pending_count || 0
    },
    {
      headerName: 'Approved',
      field: 'approved_count',
      width: '100px',
      allowSort: true,
      render: (row) => row.approved_count || 0
    },
    {
      headerName: 'Declined',
      field: 'declined_count',
      width: '100px',
      allowSort: true,
      render: (row) => row.declined_count || 0
    },
    {
      headerName: 'Last Updated',
      field: 'manager_approval_created_at',
      width: '150px',
      allowSort: true,
      render: (row) => row.manager_approval_created_at || '-'
    },
  ];

  const archivedColumns = [
    {
      headerName: 'DBA',
      field: 'powerapp_dba',
      width: '200px',
      allowSort: true,
    },
    {
      headerName: 'Organization',
      field: 'organization_name',
      width: '200px',
      allowSort: true,
    },
    {
      headerName: 'Total Approvals',
      field: 'total_approvals',
      width: '120px',
      allowSort: true,
      render: (row) => row.total_approvals || 0
    },
    {
      headerName: 'Approved',
      field: 'approved_count',
      width: '100px',
      allowSort: true,
      render: (row) => row.approved_count || 0
    },
    {
      headerName: 'Declined',
      field: 'declined_count',
      width: '100px',
      allowSort: true,
      render: (row) => row.declined_count || 0
    },
    {
      headerName: 'Last Updated',
      field: 'last_updated_ago',
      width: '150px',
      allowSort: true,
      render: (row) => row.last_updated_ago || '-'
    },
  ];

  const onRowClick = useCallback(
    (row) => {
      // If details are already shown, first hide them to reset state
      if (showDetails) {
        setShowDetails(false);
        // Small delay to ensure state is reset before showing new details
        setTimeout(() => {
          setSelectedPowerappId(row.powerapp_id);
          setShowDetails(true);
        }, 0);
      } else {
        setSelectedPowerappId(row.powerapp_id);
        setShowDetails(true);
      }
    },
    [showDetails, setShowDetails, setSelectedPowerappId],
  );

  const handleSort = useCallback((field) => {
    // Calculate new direction first (toggle if same field, otherwise default to ASC)
    const newDirection = (sortBy === field && sortDirection === 'ASC') ? 'DESC' : 'ASC';
    setSortDirection(newDirection);
    setSortBy(field);
    loadData(organizationId, currentPage, searchQuery, field, newDirection);
  }, [sortDirection, sortBy, setSortDirection, setSortBy, loadData, organizationId, currentPage, searchQuery]);

  const onChangeSearchInput = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the loadData call
    debounceTimeoutRef.current = setTimeout(() => {
      loadData(organizationId, 1, value, sortBy, sortDirection);
    }, 500);
  }, [setSearchQuery, setCurrentPage, loadData, organizationId, sortBy, sortDirection]);

  const handleShowArchivedChange = useCallback(() => {
    setShowArchived(prev => !prev);
  }, []);

  const handlePage = useCallback((pageValue) => {
    const newPage = pageValue.selected + 1;
    setCurrentPage(newPage);
    loadData(organizationId, newPage, searchQuery, sortBy, sortDirection);
  }, [setCurrentPage, loadData, organizationId, searchQuery, sortBy, sortDirection]);

  // Get data based on current view
  const tableData = showArchived ? (archivedApprovals || []) : (powerappsWithApprovals || []);

  return (
    <>
      <div className={`${pageHeadingStyles.page_heading} ${pageHeadingStyles.column} ${styles.header_wrapper}`}>
        <div className={styles.title_row}>
          <div className={pageHeadingStyles.title_contents}>
            <p>Manager Approval</p>
          </div>
          {!showDetails && (
            <div className={styles.switch_wrapper}>
              <Switch
                label="Show Archived"
                checked={showArchived}
                onChange={handleShowArchivedChange}
              />
            </div>
          )}
        </div>
        {!showDetails && (
          <div className={styles.filters_row}>
            <div className={styles.search_wrapper}>
              <SearchInput
                onChange={onChangeSearchInput}
                searchValue={searchQuery}
              />
            </div>
          </div>
        )}
      </div>

      {showArchived && !showDetails && (
        <ApprovalStats loading={loading} stats={stats} />
      )}

      <div className={styles.card}>
        <div className={styles.desktop_table}>
          <Table
            layout="fixed"
            noMarginTop
            columns={showArchived ? archivedColumns : pendingColumns}
            data={tableData}
            sortBy={sortBy}
            sortColumn={sortDirection}
            onSortClick={handleSort}
            onRowClick={onRowClick}
            enableSkeleton={true}
            dataLoading={loading}
          />
        </div>

        <div className={styles.mobile_cards_container}>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <div key={`skeleton-${index}`} className={styles.mobile_card_skeleton}>
                <div className={styles.mobile_card_skeleton_left}>
                  <SkeletonLoader
                    variant="rectangular"
                    width="100%"
                    height="16px"
                    className={styles.skeleton_rect}
                  />
                  {showArchived && (
                    <SkeletonLoader
                      variant="rectangular"
                      width="70%"
                      height="12px"
                      className={styles.skeleton_rect}
                    />
                  )}
                  <SkeletonLoader
                    variant="rectangular"
                    width="80%"
                    height="12px"
                    className={styles.skeleton_rect}
                  />
                </div>
                <div className={styles.mobile_card_skeleton_right}>
                  <SkeletonLoader
                    variant="rectangular"
                    width="32px"
                    height="32px"
                    className={styles.skeleton_badge}
                  />
                </div>
              </div>
            ))
          ) : tableData.length === 0 ? (
            <div className={styles.noData}>No data to display</div>
          ) : (
            tableData.map((powerapp) => (
              <PowerappApprovalCard
                key={powerapp.powerapp_id || powerapp.id}
                powerapp={powerapp}
                onClick={() => onRowClick(powerapp)}
                isSelected={selectedPowerappId === powerapp.powerapp_id}
                showArchived={showArchived}
              />
            ))
          )}
        </div>

        {!loading && tableData?.length > 0 && (
          <div className={styles.pagination_container}>
            <Pagination
              pageCount={pageCount}
              currentPage={currentPage - 1}
              onPageChange={handlePage}
              totalItems={showArchived ? stats.total_approvals : (totalItems || 0)}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default withRouter(List);